import axiosInstance from "../helpers/axiosHelper";
import { endpointsMetadata } from "../routes/endpointsMetadata";

// How a tapped notification's link reaches the app. The values are repeated in
// public/service-worker.js, which cannot import app code: keep the two in sync.

/** The message the service worker sends an open app: "open this link". */
export const NOTIFICATION_CLICK_MESSAGE_TYPE = "planny:notification-click";

/**
 * Where the service worker also leaves the link. iOS often fails to deliver the message,
 * or opens the app before the worker can reach it, so the app looks here whenever it
 * starts or comes to the front.
 */
export const PENDING_NOTIFICATION_CACHE = "planny-notification-click";
export const PENDING_NOTIFICATION_KEY = "/__planny/pending-notification-link";

/** A link left longer ago than this is from an earlier tap and is not opened any more. */
export const PENDING_NOTIFICATION_MAX_AGE_MS = 2 * 60 * 1000;

/**
 * Added by the API to the link an iPhone opens by itself (Declarative Web Push), which
 * skips the service worker: the app confirms "Opened" for that delivery instead.
 */
export const PUSH_OPENED_PARAM = "pushOpened";

/** A delivery token as the API writes it (a GUID without dashes). */
const PUSH_OPENED_TOKEN_PATTERN = /^[0-9a-f]{32}$/i;

export interface IPendingNotificationLink {
  url: string;
  createdAt: number;
}

/** The in-app path (path, query, hash) of a notification link, or null when it leads elsewhere. */
export const getInAppPath = (url: string, origin: string): string | null => {
  try {
    const target = new URL(url, origin);
    if (target.origin !== origin) {
      return null;
    }
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
};

export const isPendingNotificationLinkFresh = (link: IPendingNotificationLink, now: number) =>
  now - link.createdAt <= PENDING_NOTIFICATION_MAX_AGE_MS;

const isPendingNotificationLink = (value: unknown): value is IPendingNotificationLink =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as IPendingNotificationLink).url === "string" &&
  typeof (value as IPendingNotificationLink).createdAt === "number";

const openPendingNotificationCache = async (): Promise<Cache | null> => {
  if (typeof caches === "undefined" || !caches) {
    return null;
  }
  try {
    return await caches.open(PENDING_NOTIFICATION_CACHE);
  } catch {
    return null;
  }
};

/** Reads and removes the link a tapped notification left for the app, so it opens only once. */
export const takePendingNotificationLink = async (): Promise<IPendingNotificationLink | null> => {
  const cache = await openPendingNotificationCache();
  if (!cache) {
    return null;
  }
  try {
    const response = await cache.match(PENDING_NOTIFICATION_KEY);
    if (!response) {
      return null;
    }
    await cache.delete(PENDING_NOTIFICATION_KEY);
    const link: unknown = await response.json();
    return isPendingNotificationLink(link) ? link : null;
  } catch {
    return null;
  }
};

/**
 * Loads a notification's page in full, as the worker's client.navigate() did: pages act on
 * their link once per load (the waiting-list confirmation confirms on load, ?selectedStudio=
 * switches the studio once per address), so moving there through the router could leave the
 * previous result on screen.
 */
export const loadAppPage = (path: string): void => {
  window.location.assign(path);
};

/** Removes the left link once the app opened it from the worker's message. */
export const clearPendingNotificationLink = async (): Promise<void> => {
  const cache = await openPendingNotificationCache();
  try {
    await cache?.delete(PENDING_NOTIFICATION_KEY);
  } catch {
    // Nothing left to clear.
  }
};

/** Splits our "pushOpened" token off an address; the path is what stays in the address bar. */
export const splitPushOpenedToken = (href: string): { token: string | null; path: string } => {
  const url = new URL(href);
  const token = url.searchParams.get(PUSH_OPENED_PARAM);
  if (token === null) {
    return { token: null, path: `${url.pathname}${url.search}${url.hash}` };
  }
  url.searchParams.delete(PUSH_OPENED_PARAM);
  return {
    token: PUSH_OPENED_TOKEN_PATTERN.test(token) ? token : null,
    path: `${url.pathname}${url.search}${url.hash}`,
  };
};

/**
 * Called once before the router starts. When an iPhone opened a notification's link by itself,
 * confirms "Opened" for that device's delivery and takes the token out of the address, so it is
 * neither confirmed twice nor carried into the rest of the app.
 */
export const confirmOpenedPushFromLocation = (): void => {
  if (!window.location.search.includes(PUSH_OPENED_PARAM)) {
    return;
  }
  const { token, path } = splitPushOpenedToken(window.location.href);
  try {
    window.history.replaceState(window.history.state, "", path);
  } catch {
    // A crafted "//other.site/…" address cannot be rewritten; the app must still start.
  }
  if (!token) {
    return;
  }
  axiosInstance
    .post(`${endpointsMetadata.pushNotifications.opened}/${token}`)
    .catch(() => undefined);
};
